import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';

interface ComplianceHealthScoreProps {
  /** null when the team has no data yet — render an onboarding empty state */
  score: number | null;
  breakdown: {
    marketing: number;
    hmda: number;
    fairLending: number;
    exams: number;
    regulatory: number;
  };
}

const moduleLabels: Record<string, string> = {
  marketing: 'Marketing',
  hmda: 'HMDA',
  fairLending: 'Fair Lending',
  cra: 'CRA',
  exams: 'Exams',
  regulatory: 'Regulatory',
};

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-amber-500';
  return 'text-red-600';
}

function barColor(score: number): string {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-amber-500';
  return 'bg-red-500';
}

export default function ComplianceHealthScore({
  score,
  breakdown,
}: ComplianceHealthScoreProps) {
  // Empty team state — backend returns score=null when there's no
  // marketing, HMDA, fair lending, exam, or CRA data anywhere. The
  // previous default-fallback computation produced ~55% which read as
  // a real failing grade for a brand-new team. Show an onboarding CTA
  // instead.
  if (score === null) {
    return (
      <div>
        <div className="flex flex-col items-center mb-6">
          <span className="text-5xl font-bold text-gray-300">—</span>
          <span className="text-sm text-gray-500 mt-1">Compliance Health</span>
        </div>
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-600 mb-3">
            Your compliance health score will appear here once you upload your first
            HMDA file or run your first marketing scan.
          </p>
          <Link
            to="/hmda/lar-validation"
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700"
          >
            <CloudArrowUpIcon className="h-4 w-4" />
            Get Started
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Large score display */}
      <div className="flex flex-col items-center mb-6">
        <span className={clsx('text-5xl font-bold', scoreColor(score))}>
          {score}
        </span>
        <span className="text-sm text-gray-500 mt-1">Compliance Health</span>
      </div>

      {/* Module breakdown bars */}
      <div className="space-y-3">
        {Object.entries(breakdown).map(([key, value]) => (
          <div key={key}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-700">
                {moduleLabels[key] || key}
              </span>
              <span className={clsx('text-sm font-semibold', scoreColor(value))}>
                {value}
              </span>
            </div>
            <div className="bg-gray-100 rounded-full h-2">
              <div
                className={clsx('rounded-full h-2 transition-all', barColor(value))}
                style={{ width: `${Math.min(value, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
